import { faLockKeyhole } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Button from '@sharedComponents/Button';
import FlexBox from '@sharedComponents/FlexBox';
import InformationMessage from '@sharedComponents/InformationMessage';
import InputText from '@sharedComponents/InputText';
import Select from '@sharedComponents/Select';
import Typography from '@sharedComponents/Typography';
import { formatDate } from '@utils/date';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getDomainOwnerOptions, getPlatformTypeOptions } from '../../helpers';

const FieldLabel = ({ label, locked = false }) => (
    <FlexBox gap="xxs" alignItems="center">
        <Typography color="neutral-primary" fontWeight="regular">
            {label}
        </Typography>
        {locked && <FontAwesomeIcon icon={faLockKeyhole} style={{ color: 'var(--text-color-neutral-tertiary)', fontSize: '12px' }} />}
    </FlexBox>
);

const InformationTab = ({ platform, onSave = () => {} } = {}) => {
    const { t } = useTranslation('bo');

    const [name, setName] = useState(platform?.name ?? '');
    const [url, setUrl] = useState(platform?.url ?? '');
    const [type, setType] = useState(platform?.type ?? 'PRODUCTION');
    const [domainOwner, setDomainOwner] = useState(platform?.domainOwner ?? 'ETESTING');
    const [active, setActive] = useState(platform?.active ?? true);
    // The platforms of an inactive client are read-only (the API refuses their modification).
    const readOnly = platform?.client?.active === false;

    const typeOptions = getPlatformTypeOptions(t);
    const domainOwnerOptions = getDomainOwnerOptions(t);
    const statusOptions = [
        { value: 'ACTIVE', label: t('Active') },
        { value: 'INACTIVE', label: t('Inactive') },
    ];

    const isDirty =
        name !== platform?.name ||
        url !== platform?.url ||
        type !== platform?.type ||
        domainOwner !== platform?.domainOwner ||
        active !== platform?.active;

    const handleSave = () => onSave({ name: name.trim(), url: url.trim(), type, domainOwner, active });

    return (
        <FlexBox flexDirection="column" gap="l">
            {readOnly && <InformationMessage icon={faLockKeyhole} text={t('The client is inactive: this platform is read-only.')} />}
            <FlexBox gap="xl" flexWrap="wrap" alignItems="flex-start">
                <FlexBox flexDirection="column" gap="m" additionnalStyles={{ flex: '1 1 260px' }}>
                    <FlexBox flexDirection="column" gap="xxs">
                        <FieldLabel label={t('Name')} />
                        <InputText value={name} onChange={(e) => setName(e.target.value)} disabled={readOnly} />
                    </FlexBox>
                    <FlexBox flexDirection="column" gap="xxs">
                        <FieldLabel label={t('Platform URL')} />
                        <InputText value={url} onChange={(e) => setUrl(e.target.value)} disabled={readOnly} />
                    </FlexBox>
                    <FlexBox flexDirection="column" gap="xxs">
                        <FieldLabel label={t('Platform type')} />
                        <Select
                            options={typeOptions}
                            value={type}
                            onChange={(opt) => setType(opt.value)}
                            isSearchable={false}
                            isDisabled={readOnly}
                        />
                    </FlexBox>
                    <FlexBox flexDirection="column" gap="xxs">
                        <FieldLabel label={t('Status')} />
                        <Select
                            options={statusOptions}
                            value={active ? 'ACTIVE' : 'INACTIVE'}
                            onChange={(opt) => setActive(opt.value === 'ACTIVE')}
                            isSearchable={false}
                            isDisabled={readOnly}
                        />
                    </FlexBox>
                </FlexBox>

                <FlexBox flexDirection="column" gap="m" additionnalStyles={{ flex: '1 1 260px' }}>
                    <FlexBox flexDirection="column" gap="xxs">
                        <FieldLabel label={t('Domain owner')} />
                        <Select
                            options={domainOwnerOptions}
                            value={domainOwner}
                            onChange={(opt) => setDomainOwner(opt.value)}
                            isSearchable={false}
                            isDisabled={readOnly}
                        />
                    </FlexBox>
                    <FlexBox flexDirection="column" gap="xxs">
                        <FieldLabel label={t('Database')} locked />
                        <InputText value={platform?.databaseName ?? ''} disabled readOnly />
                    </FlexBox>
                    <FlexBox flexDirection="column" gap="xxs">
                        <FieldLabel label={t('Created at')} locked />
                        <InputText value={platform?.createdAt ? formatDate(platform.createdAt) : ''} disabled readOnly />
                    </FlexBox>
                </FlexBox>
            </FlexBox>
            {!readOnly && (
                <FlexBox>
                    <Button label={t('Save modifications')} disabled={!isDirty} action={handleSave} />
                </FlexBox>
            )}
        </FlexBox>
    );
};

export default InformationTab;
